import type { UserData, Affiliate } from "~/types/global.types";

const LOCAL_DEV_TUNNEL = "https://c295dd9ec643.ngrok-free.app";

/** Default free trial period in days when no affiliate-specific period is set */
export const DEFAULT_TRIAL_PERIOD_DAYS = 7;

/**
 * The iCount trial line item — a contract between two files that never call
 * each other: `integrations/icount.server.ts` writes this line onto the iCount
 * document, and `routes/payment/validate.ts` reads it back off the IPN to
 * decide whether a charge starts a trial or takes money.
 *
 * Both sides MUST go through the helpers below. They exist because the two
 * drifted once: the outbound description gained a day-count prefix while the
 * inbound check still compared for exact equality with the bare label, and for
 * 11 days every trial was silently recorded as an immediate paid activation.
 */

// NOTE: Hebrew string exchanged with the iCount payment provider — do NOT translate.
// "ימי ניסיון חינם" = "free trial days".
export const TRIAL_ITEM_LABEL = "ימי ניסיון חינם";

/** iCount paypage that defers the first charge (the trial checkout). */
export const TRIAL_PAYPAGE_ID = "35";

/**
 * Description written on the trial line. The day count leads the label because
 * iCount's payment summary renders descriptions but not quantities, so a bare
 * label would tell someone about to enter a card nothing about how long they
 * get. The authoritative day count is still `quantity` — see findTrialDays.
 */
export function buildTrialItemDescription(trialDays: number): string {
  return `${trialDays} ${TRIAL_ITEM_LABEL}`;
}

/**
 * Does this item description denote the trial line? Matches on the label
 * rather than the whole string, so any prefix the checkout adds (today a day
 * count) is tolerated and legacy documents carrying the bare label still read.
 */
export function isTrialItemDescription(description: unknown): boolean {
  return (
    typeof description === "string" && description.includes(TRIAL_ITEM_LABEL)
  );
}

/** Max item slots scanned in the flat form-encoded IPN body. */
const MAX_ITEM_SLOTS = 10;

/**
 * Trial length in days for an incoming iCount IPN, or 0 when the charge is not
 * a trial start. The day count comes from the line's `quantity` — that is the
 * value iCount actually defers the first charge by (`hk_start_after`); the
 * description is display copy.
 *
 * iCount sends items either flat form-encoded:
 *   items_info[1][description]: "7 ימי ניסיון חינם"
 *   items_info[1][quantity]:    "7"
 * or, when the body is JSON, as a nested array.
 */
export function findTrialDays(body: Record<string, unknown>): number {
  // Only the trial paypage defers a charge; anything else is a real payment.
  if (body.cp !== TRIAL_PAYPAGE_ID) {
    return 0;
  }

  for (let i = 0; i < MAX_ITEM_SLOTS; i++) {
    if (isTrialItemDescription(body[`items_info[${i}][description]`])) {
      const quantity = Number(body[`items_info[${i}][quantity]`] || 0);
      if (Number.isFinite(quantity) && quantity > 0) {
        return quantity;
      }
    }
  }

  if (Array.isArray(body.items_info)) {
    for (const item of body.items_info) {
      if (isTrialItemDescription(item?.description)) {
        const quantity = Number(item?.quantity || 0);
        if (Number.isFinite(quantity) && quantity > 0) {
          return quantity;
        }
      }
    }
  }

  return 0;
}

type Plan = "monthly" | "yearly";

type BuildCheckoutOptions = {
  plan: Plan;
  user: Partial<UserData>;
  origin?: string;
  /**
   * When provided, overrides the computed domain.
   * Useful when the resolved domain is needed elsewhere (e.g. cancel URL).
   */
  domain?: string;
  cancelUrl?: string;
  /**
   * Affiliate data used for trial period calculation.
   */
  affiliateData?: Affiliate | null;
  /**
   * When true, applies a free trial period by delaying the first payment.
   * Uses affiliate's free_premium_period if available, otherwise DEFAULT_TRIAL_PERIOD_DAYS.
   */
  applyTrialPeriod?: boolean;
};

/**
 * Get the trial period in days for a user based on their affiliate.
 * Returns 0 if the user has already used their trial (premium_tested: true).
 */
export function getTrialPeriodDays(
  userData: Partial<UserData> | null | undefined,
  affiliateData: Affiliate | null | undefined
): number {
  // If user has already used their trial, no trial available
  if (userData?.premium_tested) {
    return 0;
  }
  
  // Use affiliate's trial period if set, otherwise default
  return affiliateData?.free_premium_period ?? DEFAULT_TRIAL_PERIOD_DAYS;
}

/**
 * Check if user is eligible for a free trial.
 */
export function isEligibleForTrial(userData: Partial<UserData> | null | undefined): boolean {
  return !userData?.premium_tested;
}

/**
 * Calculate the discounted price based on coupon discount amount (in shekels).
 * @param originalPrice - The original price
 * @param discountAmount - The discount amount in shekels (NOT percentage)
 */
export function calculateDiscountedPrice(
  originalPrice: number,
  discountAmount: number | null | undefined
): number {
  if (!discountAmount || discountAmount <= 0) {
    return originalPrice;
  }
  const discounted = originalPrice - discountAmount;
  return discounted > 0 ? Math.round(discounted) : 0;
}

const PLAN_CONFIG: Record<Plan, { base: string; campaign: string }> = {
  yearly: {
    base: "https://app.icount.co.il/m/803a3/c6926f59p11u691b354",
    campaign: "17",
  },
  monthly: {
    base: "https://app.icount.co.il/m/1b323/c6926f59pcu69078727",
    campaign: "12",
  },
};

type BuildPlannerOnboardingCheckoutOptions = {
  user: Partial<UserData>;
  origin?: string;
  /** When provided, overrides computed domain. */
  domain?: string;
  /** Selected planner plan tier (number of free client users). */
  tier?: "100" | "300";
  /** Paypage language code (e.g. "en"). Only sent when non-default (English). */
  lang?: string;
};

const PLANNER_ONBOARDING_CONFIG: Record<"100" | "300", { base: string; campaign: string }> = {
  "100": {
    base: "https://app.icount.co.il/m/af033/c6926f59p19u6966085",
    campaign: "25",
  },
  "300": {
    base: "https://app.icount.co.il/m/f4324/c6926f59p1eu6966085",
    campaign: "30",
  },
} as const;

export function resolveCheckoutDomain(origin?: string): string {
  if (!origin && typeof window !== "undefined") {
    origin = window.location.origin;
  }
  if (!origin) {
    return LOCAL_DEV_TUNNEL;
  }
  const normalized = origin.toLowerCase();
  if (normalized.includes("localhost") || normalized.includes("127.0.0.1")) {
    return LOCAL_DEV_TUNNEL;
  }
  return origin.replace(/\/$/, "");
}

export function readUserDataFromStorage(): Partial<UserData> | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem("userData");
    if (!stored || stored === "undefined") return null;
    return JSON.parse(stored) as Partial<UserData>;
  } catch (e) {
    console.error("Failed to parse userData from localStorage:", e);
    return null;
  }
}

/**
 * @deprecated Use the server-side `/payment/checkout` route instead.
 * This function builds URLs for static iCount paypages. The new approach
 * uses the iCount API's paypage/generate_sale endpoint for custom pricing,
 * discounts, and trial periods via `hk_start_after`.
 */
export function buildICountCheckoutUrl({
  plan,
  user,
  origin,
  domain,
  cancelUrl,
  affiliateData,
  applyTrialPeriod,
}: BuildCheckoutOptions): string {
  const resolvedDomain = (domain ?? resolveCheckoutDomain(origin)).replace(/\/$/, "");
  const { base, campaign } = PLAN_CONFIG[plan];
  const params = new URLSearchParams({
    utm_source: "iCount",
    utm_medium: "paypage",
    utm_campaign: campaign,
    email: user.email || "",
    vat_id: user.id_number || "",
    zip: user.id ? String(user.id) : "",
    success_url: `${resolvedDomain}/payment/success`,
    failure_url: `${resolvedDomain}/payment/failure`,
    ipn_url: `${resolvedDomain}/payment/validate`,
  });

  if (cancelUrl) {
    params.set("cancel_url", cancelUrl);
  }

  const subscriptionData = (user.subscription_data as Record<string, any> | null) || null;
  const paymentData = (user.payment_data as Record<string, any> | null) || null;
  const rawState = String(subscriptionData?.state || "").toLowerCase();
  const subscriptionPlan = String(subscriptionData?.plan || "").toLowerCase();
  const rawNextPayment = subscriptionData?.next_payment_date || paymentData?.next_payment_date;

  // For trial period, use payment_date with calculated date based on trial days
  let hasTrialPaymentDate = false;
  if (applyTrialPeriod && isEligibleForTrial(user)) {
    const trialDays = getTrialPeriodDays(user, affiliateData);
    if (trialDays > 0) {
      const paymentStartDate = new Date();
      paymentStartDate.setDate(paymentStartDate.getDate() + trialDays);
      params.set("payment_date", paymentStartDate.toISOString().slice(0, 10));
      hasTrialPaymentDate = true;
    }
  }

  // For existing subscription scenarios (upgrade/cancelation), use hk_start_date with specific date
  if (!hasTrialPaymentDate && (rawState === "pending_cancelation" || subscriptionPlan === "monthly") && rawNextPayment) {
    try {
      const date =
        typeof rawNextPayment === "number"
          ? new Date(rawNextPayment * (rawNextPayment < 1e12 ? 1000 : 1))
          : new Date(rawNextPayment);
      const hkStartDate = date.toISOString().slice(0, 10);
      params.set("payment_date", hkStartDate);
    } catch {
      // no-op: unable to parse next payment date
    }
  }

  return `${base}?${params.toString()}#coupon-code`;
}

/**
 * Planner onboarding checkout (separate iCount paypage).
 * Uses dedicated success/failure/ipn routes under `/planner/payment/*`.
 */
export function buildICountPlannerOnboardingCheckoutUrl({
  user,
  origin,
  domain,
  tier = "100",
  lang,
}: BuildPlannerOnboardingCheckoutOptions): string {
  const resolvedDomain = (domain ?? resolveCheckoutDomain(origin)).replace(/\/$/, "");
  const { base, campaign } = PLANNER_ONBOARDING_CONFIG[tier];
  const params = new URLSearchParams({
    utm_source: "iCount",
    utm_medium: "paypage",
    utm_campaign: campaign,
    email: user.email || "",
    vat_id: user.id_number || "",
    zip: user.id ? String(user.id) : "",
    success_url: `${resolvedDomain}/planner/payment/success`,
    failure_url: `${resolvedDomain}/planner/payment/failure`,
    ipn_url: `${resolvedDomain}/planner/payment/validate`,
  });
  if (lang) {
    params.set("lang", lang);
  }
  return `${base}?${params.toString()}`;
}

export function safeStoreJson(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  if (value === undefined) {
    window.localStorage.removeItem(key);
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Failed to persist ${key} to localStorage:`, e);
  }
}

